import React from 'react';
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { auth } from '../../Api';
import { formatServerMessage } from '../../utils/serverErrors';
import { rateProblem, startDateProblem } from '../../utils/billing';

// The super admin's "Edit billing" dialog (laravel BillingController@saveTerms): the price per duty start (blank:
// the default) and the billing start date (blank: not billed yet) of one company, or of several at once (then each
// field is changed only when its box is ticked). Used on the Billing page and on a company's Billing tab.
// props: companies [{ id, name, company_rate_usd, starts_on }], defaultRate "0.10", onClose(), onSaved(message)
class EditBillingDialog extends React.Component {
	constructor(props) {
		super(props);
		const companies = props.companies || [];
		const one = companies.length === 1 ? companies[0] : null;
		this.state = {
			rate: one && one.company_rate_usd ? one.company_rate_usd : '',
			startsOn: one && one.starts_on ? one.starts_on : '',
			setRate: !!one,
			setStart: !!one,
			problem: null,
			busy: false
		};
	}

	save() {
		const { rate, startsOn, setRate, setStart } = this.state;
		const problem = !setRate && !setStart ? 'Tick what to change.'
			: (setRate && rateProblem(rate)) || (setStart && startDateProblem(startsOn)) || null;
		if (problem) {
			this.setState({ problem });
			return;
		}
		const request = { company_ids: this.props.companies.map((company) => company.id) };
		if (setRate) {
			request.rate_usd = String(rate).trim();
		}
		if (setStart) {
			request.billing_starts_on = startsOn || '';
		}
		this.setState({ busy: true, problem: null });
		auth.saveBillingTerms(request)
			.then((res) => {
				if (res.data.code == 200) {
					this.props.onSaved(formatServerMessage(res.data.message, 'Saved.'));
				} else {
					this.setState({ busy: false, problem: formatServerMessage(res.data.message, 'It could not be saved.') });
				}
			})
			.catch(() => this.setState({ busy: false, problem: 'It could not be saved. Please try again.' }));
	}

	render() {
		const { companies, defaultRate, onClose } = this.props;
		const { rate, startsOn, setRate, setStart, problem, busy } = this.state;
		const several = companies.length > 1;
		const title = several ? 'Edit billing of ' + companies.length + ' companies' : 'Edit billing: ' + companies[0].name;
		return (
			<Modal isOpen={true} toggle={onClose}>
				<ModalHeader toggle={onClose}>{title}</ModalHeader>
				<ModalBody>
					{several && (
						<p className="small text-muted mb-2">
							{companies.slice(0, 8).map((company) => company.name).join(', ')}
							{companies.length > 8 ? ' and ' + (companies.length - 8) + ' more' : ''}. Tick what to change for all of them.
						</p>
					)}
					<FormGroup className="mb-2">
						<Label for="edit-billing-rate" className="mb-1">
							{several && <Input type="checkbox" className="position-static mr-2" checked={setRate} aria-label="Change the price"
								onChange={(e) => this.setState({ setRate: e.target.checked, problem: null })} />}
							Price per duty start (USD)
						</Label>
						<Input id="edit-billing-rate" bsSize="sm" type="text" inputMode="decimal" value={rate} disabled={!setRate}
							placeholder={'Blank: the default, ' + defaultRate}
							onChange={(e) => this.setState({ rate: e.target.value, problem: null })} />
						<small className="text-muted">Blank: the default price, USD {defaultRate} now (it follows the default if that changes).</small>
					</FormGroup>
					<FormGroup className="mb-2">
						<Label for="edit-billing-start" className="mb-1">
							{several && <Input type="checkbox" className="position-static mr-2" checked={setStart} aria-label="Change the billing start date"
								onChange={(e) => this.setState({ setStart: e.target.checked, problem: null })} />}
							Billing starts on
						</Label>
						<Input id="edit-billing-start" bsSize="sm" type="date" value={startsOn} disabled={!setStart}
							onChange={(e) => this.setState({ startsOn: e.target.value, problem: null })} />
						<small className="text-muted">
							{startsOn ? 'Duty starts from this day (company time) are billed.' : 'Blank: not billed yet.'}
						</small>
					</FormGroup>
					<Alert color="warning" className="small mb-0 py-2">
						Changing the price <strong>does not change invoices already sent</strong>; to correct one, use Void and reissue on the
						company's Billing tab. A new price applies to duty starts recorded from now on (those already recorded this month keep
						their price). A new start date applies from the next invoice.
					</Alert>
					{problem && <div className="text-danger mt-2">{problem}</div>}
				</ModalBody>
				<ModalFooter>
					<Button color="primary" disabled={busy} onClick={() => this.save()}>Save</Button>
					<Button color="secondary" onClick={onClose}>Cancel</Button>
				</ModalFooter>
			</Modal>
		);
	}
}

export default EditBillingDialog;
